import { Injectable, inject } from '@angular/core';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { APP_VERSION } from '@principle-theorem/ng-shared';
import {
  AccInvoiceFundingRequest,
  Invoice,
  Patient,
  Practice,
} from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IAccInvoiceBuilderClaimData,
  IAccInvoiceFundingRequest,
  IClaimableItem,
  IHealthcareClaim,
  IInvoice,
  IPatient,
  IPractice,
  IPracticeAccSettings,
  isEventable,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  addDocAsWithRef,
  filterUndefined,
  snapshot,
  toISODate,
  toNamedDocument,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Observable, combineLatest } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AccApiService } from './acc-api.service';
import { AccInvoiceBuilder } from './acc-invoice-builder';

@Injectable()
export class AccInvoiceService {
  private _organisation = inject(OrganisationService);
  private _accApi = inject(AccApiService);
  private _practice$ = this._organisation.practice$.pipe(filterUndefined());
  private _appVersion = inject(APP_VERSION);

  getAccInvoices$(
    patient$: Observable<WithRef<IPatient> | undefined>
  ): Observable<WithRef<IAccInvoiceFundingRequest>[]> {
    return combineLatest([this._practice$, patient$]).pipe(
      switchMap(([practice, patient]) =>
        patient
          ? Patient.withPatientRelationships$(
              patient,
              [PatientRelationshipType.DuplicatePatient],
              (relatedPatient) =>
                AccInvoiceFundingRequest.byPatient$(practice, relatedPatient)
            )
          : AccInvoiceFundingRequest.all$(practice)
      )
    );
  }

  async generateInvoice(
    claimData: IAccInvoiceBuilderClaimData,
    claimItems: IClaimableItem[],
    claim: IHealthcareClaim,
    invoice: WithRef<IInvoice>,
    patient: WithRef<IPatient>
  ): Promise<WithRef<IAccInvoiceFundingRequest> | undefined> {
    const practice = await snapshot(this._practice$);
    if (!practice.accSettings) {
      return;
    }

    const fundingRequest = AccInvoiceFundingRequest.init({
      patient: toNamedDocument(patient),
      status: FundingRequestStatus.Pending,
      data: {
        claimUid: claim.uid,
      },
    });
    const invoiceRequest = await addDocAsWithRef(
      Practice.fundingRequestCol<IAccInvoiceFundingRequest>(practice),
      fundingRequest
    );

    void this._submitInvoice(
      claimData,
      invoiceRequest,
      claimItems,
      invoice,
      practice,
      practice.accSettings
    );

    return invoiceRequest;
  }

  async deleteInvoice(
    invoiceRequest: WithRef<IAccInvoiceFundingRequest>
  ): Promise<void> {
    await Firestore.deleteDoc(invoiceRequest.ref);
  }

  private async _submitInvoice(
    claimData: IAccInvoiceBuilderClaimData,
    invoiceRequest: WithRef<IAccInvoiceFundingRequest>,
    claimItems: IClaimableItem[],
    invoice: WithRef<IInvoice>,
    practice: WithRef<IPractice>,
    accSettings: IPracticeAccSettings
  ): Promise<void> {
    const appointment = await Invoice.getAssociatedAppointment(invoice);
    const serviceDate =
      appointment && isEventable(appointment)
        ? toISODate(appointment.event.from)
        : toISODate(moment());
    const invoiceData = AccInvoiceBuilder.build(
      claimData,
      claimItems,
      practice,
      accSettings,
      serviceDate,
      this._appVersion
    );

    await this._accApi.submitInvoice(invoiceData, invoiceRequest.ref);
  }
}
